import React, { useState, useEffect, useCallback } from 'react';
import { getPreventivi } from '../services/api';
import { Card, Row, Col, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import StandardPageHeader from '../common/StandardPageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faFileInvoice, faRotate, faInbox } from '@fortawesome/free-solid-svg-icons';

// Vista dedicata alle richieste che arrivano dal sito riversamenti: solo lo stato
// "Richiesto", in sola lettura. La lavorazione vera resta su PreventivoEdit.
const STATO_RICHIESTO = 'Richiesto';

const formatEuro = (valore) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(valore || 0);

const formatData = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const telHref = (tel) => {
  const cifre = (tel || '').replace(/[^\d+]/g, '');
  if (!cifre) return null;
  return `tel:${cifre.startsWith('+') ? cifre : `+39${cifre}`}`;
};

const RichiesteRiversamenti = () => {
  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const caricaRichieste = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPreventivi(0, 200, [], { stato: STATO_RICHIESTO });
      const lista = response.data?.preventivi || [];
      lista.sort((a, b) => new Date(b.data_creazione) - new Date(a.data_creazione));
      setRichieste(lista);
    } catch (err) {
      console.error('Errore caricamento richieste riversamenti:', err);
      setError('Impossibile caricare le richieste. Riprova.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    caricaRichieste();
  }, [caricaRichieste]);

  return (
    <div className="p-2 p-md-3">
      <StandardPageHeader title="Richieste riversamenti" />

      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        <span className="text-muted">
          {loading
            ? 'Caricamento...'
            : `${richieste.length} ${richieste.length === 1 ? 'richiesta' : 'richieste'} in attesa di lavorazione`}
        </span>
        <Button variant="outline-secondary" size="sm" onClick={caricaRichieste} disabled={loading}>
          <FontAwesomeIcon icon={faRotate} className="me-2" />
          Aggiorna
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : richieste.length === 0 ? (
        <Card className="text-center py-5 border-0 shadow-sm">
          <Card.Body>
            <FontAwesomeIcon icon={faInbox} size="2x" className="text-muted mb-3" />
            <h5 className="text-muted">Nessuna richiesta in attesa</h5>
            <p className="text-muted mb-0">
              Le nuove richieste dal sito compaiono qui in stato &laquo;{STATO_RICHIESTO}&raquo;.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3">
            {richieste.map((p) => {
              const tel = telHref(p.cliente_telefono);
              return (
                <Col key={p.id} xs={12} lg={6}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="min-width-0">
                          <h5 className="mb-1">{p.cliente_nome || 'Senza nome'}</h5>
                          <div className="text-muted small">{formatData(p.data_creazione)}</div>
                        </div>
                        <Badge bg="warning" text="dark">
                          #{p.id}
                        </Badge>
                      </div>

                      <ul className="list-unstyled mt-3 mb-3">
                        {(p.dettagli || []).map((d) => (
                          <li key={d.id} className="d-flex justify-content-between gap-3 border-bottom py-1">
                            <span>
                              <strong>{d.quantita}&times;</strong> {d.descrizione}
                            </span>
                            <span className="text-nowrap">
                              {formatEuro((d.prezzo_unitario || 0) * (d.quantita || 0))}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {p.note && (
                        <Alert variant="light" className="py-2 px-3 small mb-3 border">
                          <strong>Note del cliente:</strong> {p.note}
                        </Alert>
                      )}

                      <div className="d-flex justify-content-between align-items-baseline mb-3">
                        <span className="text-muted small">Stima dal listino</span>
                        <strong className="fs-5">{formatEuro(p.totale)}</strong>
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        {tel && (
                          <Button as="a" href={tel} variant="success" size="sm">
                            <FontAwesomeIcon icon={faPhone} className="me-2" />
                            {p.cliente_telefono}
                          </Button>
                        )}
                        {p.cliente_email && (
                          <Button
                            as="a"
                            href={`mailto:${p.cliente_email}`}
                            variant="outline-secondary"
                            size="sm"
                          >
                            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                            {p.cliente_email}
                          </Button>
                        )}
                        <Button
                          as={Link}
                          to={`/preventivi/edit/${p.id}`}
                          variant="primary"
                          size="sm"
                          className="ms-auto"
                        >
                          <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                          Apri preventivo
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </div>
  );
};

export default RichiesteRiversamenti;
